import type Fuse from 'fuse.js';

import type {OmniboxFilter} from 'omnibox/useTypeaheadData';

import {MAX_TYPEAHEAD_RESULTS, getModelTypeDisplayName} from './omniboxConstants';

export function extractFilterParams(
  selectedFilters: Array<{
    kind: string;
    id: string;
    searchTypes?: ReadonlyArray<string>;
    schemaApiIds?: ReadonlyArray<string>;
  }>
): {
  schemaApiIds: Array<string>;
  objectTypes: Array<string>;
  // Global search types a Type dropdown selection narrows `allowedTypes` to; empty when none.
  searchTypes: Array<string>;
  projectApiIds: Array<string>;
  folderApiIds: Array<string>;
  authorApiIds: Array<string>;
} {
  return {
    schemaApiIds: [
      ...new Set(
        selectedFilters.flatMap((f) =>
          f.kind === 'schema' ? [f.id] : f.kind === 'type' ? (f.schemaApiIds ?? []) : []
        )
      ),
    ],
    objectTypes: selectedFilters.filter((f) => f.kind === 'type' && f.searchTypes == null).map((f) => f.id),
    searchTypes: [...new Set(selectedFilters.flatMap((f) => f.searchTypes ?? []))],
    projectApiIds: selectedFilters.filter((f) => f.kind === 'project').map((f) => f.id),
    folderApiIds: selectedFilters.filter((f) => f.kind === 'folder').map((f) => f.id),
    authorApiIds: selectedFilters.filter((f) => f.kind === 'user').map((f) => f.id),
  };
}

const FILTER_CHIP_PREFIXES: Record<OmniboxFilter['kind'], string> = {
  user: 'Authored by',
  project: 'In project',
  folder: 'In folder',
  type: 'Type',
  schema: 'Schema',
};

export function getFilterChipLabel(filter: OmniboxFilter): string {
  return `${FILTER_CHIP_PREFIXES[filter.kind]}: ${filter.name}`;
}

export function getFilterDescription(item: OmniboxFilter): string | undefined {
  if (item.kind === 'user') {
    return item.isSelf ? 'You' : `@${item.handle}`;
  }
  if (item.kind === 'project' || item.kind === 'folder') {
    return item.projectName;
  }
  if (item.kind === 'schema' && item.modelType) {
    return getModelTypeDisplayName(item.modelType);
  }
  return undefined;
}

export function filterAndSearchTypeahead(
  items: Array<OmniboxFilter>,
  fuseInstance: Fuse<OmniboxFilter>,
  query: string,
  selectedIds: Set<string>,
  maxResults: number = MAX_TYPEAHEAD_RESULTS
): Array<OmniboxFilter> {
  if (!query) {
    return items.filter((item) => !selectedIds.has(`${item.kind}:${item.id}`)).slice(0, maxResults);
  }
  return fuseInstance
    .search(query)
    .map((r) => r.item)
    .filter((item) => !selectedIds.has(`${item.kind}:${item.id}`))
    .slice(0, maxResults);
}
